"use client"

import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { getSchool, updateSchool } from "@/hooks/api/schoolApi"
import { Check, Loader2, AlertCircle } from "lucide-react"

export function AccountSettingsTab() {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [name, setName] = useState("")
  const [phone, setPhone] = useState("")
  const [website, setWebsite] = useState("")
  const [principalName, setPrincipalName] = useState("")
  const [principalEmail, setPrincipalEmail] = useState("")
  const [principalPhone, setPrincipalPhone] = useState("")
  const [motto, setMotto] = useState("")
  const [mission, setMission] = useState("")
  const [street, setStreet] = useState("")
  const [city, setCity] = useState("")
  const [state, setState] = useState("")

  useEffect(() => {
    getSchool()
      .then((s: any) => {
        setName(s.name ?? "")
        setPhone(s.phone ?? "")
        setWebsite(s.website ?? "")
        setPrincipalName(s.principalName ?? "")
        setPrincipalEmail(s.principalEmail ?? "")
        setPrincipalPhone(s.principalPhone ?? "")
        setMotto(s.motto ?? "")
        setMission(s.mission ?? "")
        setStreet(s.address?.street ?? "")
        setCity(s.address?.city ?? "")
        setState(s.address?.state ?? "")
      })
      .catch(err => setError(err?.response?.data?.error?.message ?? "Could not load school profile."))
      .finally(() => setLoading(false))
  }, [])

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    try {
      await updateSchool({
        name,
        phone,
        website,
        principalName,
        principalEmail,
        principalPhone,
        motto,
        mission,
        address: { street, city, state, country: "Nigeria" },
      })
      setSaved(true)
      setTimeout(() => setSaved(false), 3000)
    } catch (err: any) {
      setError(err?.response?.data?.error?.message ?? "Save failed. Please try again.")
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 gap-3 text-slate-400">
        <Loader2 className="w-5 h-5 animate-spin" />
        <span className="text-sm">Loading school profile…</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <p className="text-sm">{error}</p>
        </div>
      )}

      {/* School Info */}
      <Card className="p-6 border border-border space-y-5">
        <h3 className="text-lg font-semibold text-foreground">School Information</h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <label className="text-sm font-medium text-muted-foreground block mb-1">School Name *</label>
            <Input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Greenfield Academy"
              className="border-border bg-white text-foreground"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-muted-foreground block mb-1">Phone</label>
            <Input value={phone} onChange={e => setPhone(e.target.value)} placeholder="+234 801 234 5678" className="border-border bg-white" />
          </div>
          <div>
            <label className="text-sm font-medium text-muted-foreground block mb-1">Website</label>
            <Input value={website} onChange={e => setWebsite(e.target.value)} placeholder="https://school.edu.ng" className="border-border bg-white" />
          </div>
          <div>
            <label className="text-sm font-medium text-muted-foreground block mb-1">Motto</label>
            <Input value={motto} onChange={e => setMotto(e.target.value)} placeholder="Excellence in Education" className="border-border bg-white" />
          </div>
          <div>
            <label className="text-sm font-medium text-muted-foreground block mb-1">Mission</label>
            <Input value={mission} onChange={e => setMission(e.target.value)} placeholder="Our mission statement…" className="border-border bg-white" />
          </div>
        </div>
      </Card>

      {/* Address */}
      <Card className="p-6 border border-border space-y-4">
        <h3 className="text-lg font-semibold text-foreground">Address</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="sm:col-span-3">
            <label className="text-sm font-medium text-muted-foreground block mb-1">Street</label>
            <Input value={street} onChange={e => setStreet(e.target.value)} placeholder="123 School Road" className="border-border bg-white" />
          </div>
          <div>
            <label className="text-sm font-medium text-muted-foreground block mb-1">City</label>
            <Input value={city} onChange={e => setCity(e.target.value)} placeholder="Lagos" className="border-border bg-white" />
          </div>
          <div>
            <label className="text-sm font-medium text-muted-foreground block mb-1">State</label>
            <Input value={state} onChange={e => setState(e.target.value)} placeholder="Lagos" className="border-border bg-white" />
          </div>
        </div>
      </Card>

      {/* Principal */}
      <Card className="p-6 border border-border space-y-4">
        <h3 className="text-lg font-semibold text-foreground">Principal Details</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="text-sm font-medium text-muted-foreground block mb-1">Name</label>
            <Input value={principalName} onChange={e => setPrincipalName(e.target.value)} placeholder="Mr. Emeka Okafor" className="border-border bg-white" />
          </div>
          <div>
            <label className="text-sm font-medium text-muted-foreground block mb-1">Email</label>
            <Input type="email" value={principalEmail} onChange={e => setPrincipalEmail(e.target.value)} placeholder="principal@school.edu.ng" className="border-border bg-white" />
          </div>
          <div>
            <label className="text-sm font-medium text-muted-foreground block mb-1">Phone</label>
            <Input value={principalPhone} onChange={e => setPrincipalPhone(e.target.value)} placeholder="+234 802 345 6789" className="border-border bg-white" />
          </div>
        </div>
      </Card>

      <div className="flex items-center gap-4">
        <Button onClick={handleSave} disabled={saving || !name.trim()} className="bg-primary hover:bg-primary/90 text-white px-8">
          {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</> : "Save Changes"}
        </Button>
        {saved && (
          <div className="flex items-center gap-2 text-green-600 text-sm font-medium">
            <Check className="w-4 h-4" />
            <span>Saved successfully</span>
          </div>
        )}
      </div>
    </div>
  )
}
